import { supabase } from "./supabase";

export interface WeightLog {
  id: string;
  logged_at: string;
  weight_kg: number;
  note?: string;
}

export type WeightUnit = "lbs" | "kg";

export const LBS_PER_KG = 2.2046226218;

export function kgToDisplay(kg: number, unit: WeightUnit): number {
  return unit === "kg" ? kg : kg * LBS_PER_KG;
}

export function displayToKg(value: number, unit: WeightUnit): number {
  return unit === "kg" ? value : value / LBS_PER_KG;
}

export async function loadWeights(): Promise<WeightLog[]> {
  if (!supabase) throw new Error("Supabase not configured");
  const { data, error } = await supabase
    .from("weight_logs")
    .select("*")
    .order("logged_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id,
    logged_at: r.logged_at,
    weight_kg: Number(r.weight_kg),
    note: r.note ?? undefined
  }));
}

export async function saveWeight(weight_kg: number, logged_at?: string, note?: string): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const { data: userData } = await supabase.auth.getUser();
  const user = userData.user;
  if (!user) throw new Error("Not signed in");
  const { error } = await supabase.from("weight_logs").insert({
    user_id: user.id,
    weight_kg,
    logged_at: logged_at ?? new Date().toISOString(),
    note: note ?? null
  });
  if (error) throw error;
}

export async function deleteWeight(id: string): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const { error } = await supabase.from("weight_logs").delete().eq("id", id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// user prefs (weight unit)
// ---------------------------------------------------------------------------
export async function loadWeightUnit(): Promise<WeightUnit> {
  if (!supabase) return "lbs";
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return "lbs";
  const { data } = await supabase
    .from("user_prefs")
    .select("weight_unit")
    .eq("user_id", userData.user.id)
    .maybeSingle();
  return (data?.weight_unit as WeightUnit) ?? "lbs";
}

export async function saveWeightUnit(unit: WeightUnit): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const { data: userData } = await supabase.auth.getUser();
  const user = userData.user;
  if (!user) throw new Error("Not signed in");
  const { error } = await supabase
    .from("user_prefs")
    .upsert({ user_id: user.id, weight_unit: unit, updated_at: new Date().toISOString() });
  if (error) throw error;
}
